import React, { useMemo } from "react";
import type { Instance } from "../types";
import { useFilterStore, type ColumnKey } from "../../../store/filterStore";

interface TotalsRowProps {
  selectedInstances: Instance[];
}

// Helper function to format price values (same as InstanceRow)
const formatPrice = (value: number | string): string => {
  return typeof value === "number" ? `$${value.toFixed(4)}` : value;
};

// Helper function to parse price value
const parsePrice = (value: number | string | undefined | null): number => {
  if (value === undefined || value === null || value === "-") return 0;
  if (typeof value === "number") return value;
  const parsed = parseFloat(String(value));
  return isNaN(parsed) ? 0 : parsed;
};

const COLUMN_ORDER: ColumnKey[] = [
  "instanceType",
  "instanceFamily",
  "vcpus",
  "memory",
  "cpuArchitecture",
  "gpu",
  "gpuName",
  "aws",
  "gcp",
  "azure",
  "best",
];

const TotalsRow: React.FC<TotalsRowProps> = ({ selectedInstances }) => {
  const columnVisibility = useFilterStore((state) => state.columnVisibility);

  // Calculate totals for AWS, GCP, and Azure
  const totals = useMemo(() => {
    let awsTotal = 0;
    let gcpTotal = 0;
    let azureTotal = 0;

    selectedInstances.forEach((instance) => {
      awsTotal += parsePrice(instance.aws);
      gcpTotal += parsePrice(instance.gcp);
      azureTotal += parsePrice(instance.azure);
    });

    return { aws: awsTotal, gcp: gcpTotal, azure: azureTotal };
  }, [selectedInstances]);

  // Get visible columns (same logic as InstanceRow)
  const visibleColumns = useMemo(
    () => COLUMN_ORDER.filter((key) => columnVisibility[key]),
    [columnVisibility]
  );

  // Render cell for each column type
  const renderCell = (key: ColumnKey, isFirstColumn: boolean) => {
    // Show "Total" label in the first visible column
    if (isFirstColumn) {
      return (
        <td key={key} className="px-6 py-4 text-gray-900 font-semibold bg-gray-50">
          Total
        </td>
      );
    }
    
    if (key === "aws") {
      return (
        <td key={key} className="px-6 py-4 text-gray-700 font-semibold bg-gray-50">
          {formatPrice(totals.aws)}
        </td>
      );
    }
    if (key === "gcp") {
      return (
        <td key={key} className="px-6 py-4 text-gray-700 font-semibold bg-gray-50">
          {formatPrice(totals.gcp)}
        </td>
      );
    }
    if (key === "azure") {
      return (
        <td key={key} className="px-6 py-4 text-gray-700 font-semibold bg-gray-50">
          {formatPrice(totals.azure)}
        </td>
      );
    }
    // Empty cell for non-price columns
    return (
      <td key={key} className="px-6 py-4 text-gray-700 bg-gray-50">
        -
      </td>
    );
  };

  if (selectedInstances.length === 0) {
    return null;
  }

  return (
    <tbody className="bg-gray-50 border-b-2 border-gray-300">
      <tr>
        {/* Checkbox column - empty */}
        <td className="px-6 py-4 bg-gray-50"></td>
        
        {/* Render visible columns */}
        {visibleColumns.map((key, index) => renderCell(key, index === 0))}
        
        {/* Actions column - empty */}
        <td className="px-6 py-4 bg-gray-50"></td>
      </tr>
    </tbody>
  );
};

export default TotalsRow;

